/**
 * SHER-464 follow-up — display helpers for the prescription (ordonnance) table's
 * "Moment de prise" (meal-instruction) column.
 *
 * Originally the table rendered TWO columns: the standard option and a separate
 * "Moment de prise (autre)" for the free text. That extra column was empty for
 * every non-"other" row (reads as a missing value) and, when "Autre" was picked,
 * the main column showed the raw untranslated "other" while the text sat apart.
 * These collapse it into a single cell and drop the "(autre)" column.
 */

export const MEAL_INSTRUCTION_KEY = 'treatment-meal-instruction';
export const MEAL_INSTRUCTION_OTHER_KEY = 'treatment-meal-instruction-other';
export const MEAL_INSTRUCTION_OTHER_VALUE = 'other';

type Translate = (id: string) => string;

/**
 * Value shown in the single "Moment de prise" prescription cell.
 * - standard option → its translated label (falling back to the raw value if a
 *   translation is missing, matching the renderer's existing guard);
 * - "Autre" → "Autre — <free text>" (or just "Autre" when no text was entered).
 */
export function formatMealInstructionCell(
    value: string,
    otherText: string | undefined,
    translate: Translate,
): string {
    const label = translate(`hospital.meal-options.${value}`).toString();

    if (value === MEAL_INSTRUCTION_OTHER_VALUE) {
        const text = (otherText ?? '').trim();
        return text ? `${label} — ${text}` : label;
    }

    return label.includes('Missing translationId:') ? value : label;
}

/**
 * Columns suppressed from the prescription table. `treatment-start`/`-finish` are
 * collapsed into the computed "Durée" column; `treatment-meal-instruction-other`
 * is folded into the single "Moment de prise" cell (see formatMealInstructionCell).
 */
export const PRESCRIPTION_HIDDEN_COLUMN_IDS = [
    'treatment-start',
    'treatment-finish',
    MEAL_INSTRUCTION_OTHER_KEY,
];

export function hidePrescriptionColumns<T extends { id: string }>(headCells: T[]): T[] {
    return headCells.filter((cell) => !PRESCRIPTION_HIDDEN_COLUMN_IDS.includes(cell.id));
}
